import React from 'react';

import { Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import {
  DEFAULT_CURRENCY,
  formatDealOptionCurrency,
  formatDealOptionPercent,
} from '../dealOptions/dealOptionsFormatters';

import { EGS_WRITE_CURRENCY, type EgsSlotOrigin, type RoomRateEgsPlan, isSlotChanged } from './buildEgsApplyPlan';
import { EGS_SLOTS, EGS_SLOT_LABEL, getEgsMarginPercent } from './egsUtils';

// Free of Charge is a Salesforce answer, not a separate source; a kept slot is the one Salesforce is silent on.
const ORIGIN_LABEL: Record<EgsSlotOrigin, string> = {
  free_of_charge: 'Salesforce, free of charge',
  kept: 'This room rate',
  salesforce: 'Salesforce',
};

interface Props {
  plan: RoomRateEgsPlan;
}

function AmountCell({
  cost,
  currency,
  isChanged,
  sell,
}: {
  cost: number | null;
  currency: string;
  isChanged: boolean;
  sell: number | null;
}) {
  return (
    <TableCell align="right" sx={{ color: isChanged ? 'text.primary' : 'text.secondary' }}>
      <Typography fontWeight={isChanged ? 600 : 400} variant="body2">
        {sell === null ? '—' : formatDealOptionCurrency(currency, sell)}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {cost === null ? '— cost' : `${formatDealOptionCurrency(currency, cost)} cost`}
      </Typography>
    </TableCell>
  );
}

// Before and after in one row: a slot left alone in the old currency is only obviously wrong beside the new.
export default function EgsSlotDiffTable({ plan }: Props) {
  const beforeCurrency = plan.existingCurrency ?? DEFAULT_CURRENCY;
  // `en-AU` renders AUD bare, so `$400.00` beside `USD 268.00` reads as the same unit and a re-denomination
  // looks like a price rise. Named only where the two disagree, so an AUD room rate stays free of codes.
  const isCurrencyChanging = beforeCurrency !== EGS_WRITE_CURRENCY;

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Guest type</TableCell>
          <TableCell align="right">{isCurrencyChanging ? `Now (${beforeCurrency})` : 'Now'}</TableCell>
          <TableCell align="right">
            {isCurrencyChanging ? `After apply (${EGS_WRITE_CURRENCY})` : 'After apply'}
          </TableCell>
          <TableCell align="right">Margin</TableCell>
          <TableCell>Source</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {EGS_SLOTS.map((slot) => {
          const slotPlan = plan.slots[slot];
          const isChanged = isSlotChanged(slotPlan);

          return (
            <TableRow key={slot}>
              <TableCell sx={{ color: isChanged ? 'text.primary' : 'text.secondary' }}>
                {EGS_SLOT_LABEL[slot]}
              </TableCell>
              <AmountCell
                cost={slotPlan.cost.before}
                currency={beforeCurrency}
                isChanged={isChanged}
                sell={slotPlan.sell.before}
              />
              <AmountCell
                cost={slotPlan.cost.after}
                currency={EGS_WRITE_CURRENCY}
                isChanged={isChanged}
                sell={slotPlan.sell.after}
              />
              <TableCell align="right" sx={{ color: isChanged ? 'text.primary' : 'text.secondary' }}>
                <Typography variant="body2">
                  {formatDealOptionPercent(getEgsMarginPercent(slotPlan.cost.after, slotPlan.sell.after))}
                </Typography>
              </TableCell>
              <TableCell>
                {/* Source only. Whether it changes anything is carried by the row's weight, because a
                    Salesforce figure identical to the stored one is a source without being a change. */}
                <Chip
                  color={isChanged ? 'info' : 'default'}
                  label={ORIGIN_LABEL[slotPlan.origin]}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
